const MOVING_SPEED_KMH = Number(process.env.REACT_APP_MOVING_SPEED_KMH || 5);
const MOVING_HOLD_MS = Number(process.env.REACT_APP_MOVING_HOLD_MS || 180000);
const CREEPING_SPEED_KMH = Number(process.env.REACT_APP_CREEPING_SPEED_KMH || 2);
const HEADING_FREEZE_SPEED_KMH = Number(process.env.REACT_APP_HEADING_FREEZE_SPEED_KMH || 5);
const HEADING_BEARING_MIN_DISTANCE_M = Number(process.env.REACT_APP_HEADING_BEARING_MIN_DISTANCE_M || 12);
const HEADING_MAX_STEP_DEG = Number(process.env.REACT_APP_HEADING_MAX_STEP_DEG || 65);
const TURN_MIN_SPEED_KMH = Number(process.env.REACT_APP_TURN_MIN_SPEED_KMH || 8);
const TURN_MIN_DISTANCE_M = Number(process.env.REACT_APP_TURN_MIN_DISTANCE_M || 10);
const TURN_CONFIRM_WINDOW_MS = Number(process.env.REACT_APP_TURN_CONFIRM_WINDOW_MS || 90000);

const DIRECTION_SET = new Set(["N", "NE", "E", "SE", "S", "SW", "W", "NW"]);

const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toFiniteNullable = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toBoolean = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const key = value.trim().toLowerCase();
    if (["1", "true", "on", "yes", "acc_on", "ignition_on", "engine_on", "started"].includes(key)) {
      return true;
    }
    if (["0", "false", "off", "no", "acc_off", "ignition_off", "engine_off", "stopped"].includes(key)) {
      return false;
    }
  }
  return fallback;
};

export const normalizeHeading360 = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return ((n % 360) + 360) % 360;
};

export const shortestDeltaDeg = (fromDeg, toDeg) => ((toDeg - fromDeg + 540) % 360) - 180;

const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const bearingDegrees = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1));
  return normalizeHeading360(toDeg(Math.atan2(y, x)), 0);
};

export const headingToDirection = (heading) => {
  const h = normalizeHeading360(heading, 0);
  if (h >= 337.5 || h < 22.5) return "N";
  if (h < 67.5) return "NE";
  if (h < 112.5) return "E";
  if (h < 157.5) return "SE";
  if (h < 202.5) return "S";
  if (h < 247.5) return "SW";
  if (h < 292.5) return "W";
  return "NW";
};

const classifyTurn = (absDelta) => {
  if (absDelta >= 130) return "u_turn";
  if (absDelta >= 70) return "sharp_turn";
  if (absDelta >= 35) return "turn";
  if (absDelta >= 20) return "slight_turn";
  return null;
};

export const normalizeVehicleStatus = (value) => {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return null;
  if (key === "moving") return "running";
  if (key === "idling") return "idle";
  if (key === "parked" || key === "engine_off") return "stopped";
  if (key === "inactive" || key === "no_active" || key === "no active") return "inactive";
  if (key === "offline") return null;
  if (key === "nodata" || key === "no_data" || key === "no data" || key === "unknown") return "no_data";
  if (key === "inactive_28h") return "inactive";
  if (["running", "idle", "stopped", "inactive", "expired"].includes(key)) return key;
  return null;
};

export const normalizeMovementState = (value) => {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return null;
  if (key === "running" || key === "moving") return "moving";
  if (key === "idle" || key === "idling") return "idle";
  if (key === "stopped" || key === "parked") return "stopped";
  if (key === "inactive" || key === "expired") return "inactive";
  if (key === "offline") return "unknown";
  if (key === "no_data" || key === "nodata" || key === "unknown") return "unknown";
  return null;
};

const normalizeDirection = (value) => {
  const key = String(value || "").trim().toUpperCase();
  return DIRECTION_SET.has(key) ? key : null;
};

const normalizeTurnType = (value) => {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return null;
  if (["u_turn", "sharp_turn", "turn", "slight_turn"].includes(key)) return key;
  return null;
};

const normalizeMovementStatus = (value) => {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return null;
  if (key === "running") return "running";
  if (key === "idling" || key === "idle") return "idle";
  if (key === "stopped") return "stopped";
  return null;
};

const inferVehicleStatus = ({ vehicleStatus, movementStatus, speed, accStatus, prev, timestampMs }) => {
  const normalizedMovementStatus = normalizeMovementStatus(movementStatus);
  if (normalizedMovementStatus) return normalizedMovementStatus;

  const normalized = normalizeVehicleStatus(vehicleStatus);
  if (normalized === "inactive" || normalized === "expired" || normalized === "no_data") return normalized;
  if (normalized === "stopped") return "stopped";
  if (normalized === "idle") return speed >= MOVING_SPEED_KMH ? "running" : "idle";
  if (normalized === "running" && accStatus === false && speed < MOVING_SPEED_KMH) return "stopped";
  if (normalized === "running") return "running";

  const prevLastMovingAtMs = Number(prev?.lastMovingAtMs || prev?.deviceTimestampMs || 0);
  const movingHeld =
    prevLastMovingAtMs > 0 &&
    Number.isFinite(Number(timestampMs)) &&
    Number(timestampMs) - prevLastMovingAtMs < MOVING_HOLD_MS;

  if (speed >= MOVING_SPEED_KMH) return "running";
  if (accStatus === true) return "idle";
  if (movingHeld) return "running";
  if (accStatus === false) return "stopped";
  if (normalized) return normalized;
  return "stopped";
};

const resolveMovementState = ({ vehicleStatus, movementStatus, speed, accStatus, lastMovingAtMs, timestampMs }) => {
  const normalizedMovementStatus = normalizeMovementStatus(movementStatus);
  if (normalizedMovementStatus === "running") return "moving";
  if (normalizedMovementStatus === "idle") return "idle";
  if (normalizedMovementStatus === "stopped") return "stopped";

  if (vehicleStatus === "inactive" || vehicleStatus === "expired") return "inactive";
  if (vehicleStatus === "no_data") return "unknown";
  if (vehicleStatus === "running") return "moving";
  if (vehicleStatus === "idle") return speed >= CREEPING_SPEED_KMH ? "moving" : "idle";
  if (vehicleStatus === "stopped") {
    if (
      lastMovingAtMs > 0 &&
      Number.isFinite(Number(timestampMs)) &&
      Number(timestampMs) - lastMovingAtMs < MOVING_HOLD_MS
    ) {
      return "moving";
    }
    return "stopped";
  }
  if (accStatus === false) return "stopped";
  if (accStatus === true) return speed >= CREEPING_SPEED_KMH ? "moving" : "idle";
  return "unknown";
};

const resolveHeading = ({ prev, latitude, longitude, speed, heading }) => {
  const prevHasCoord = Number.isFinite(Number(prev?.latitude)) && Number.isFinite(Number(prev?.longitude));
  const currHasCoord = Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));
  const prevHeading = Number.isFinite(Number(prev?.heading)) ? normalizeHeading360(prev.heading, 0) : null;
  const packetHeading = Number.isFinite(Number(heading)) ? normalizeHeading360(heading, prevHeading ?? 0) : null;

  let movementMeters = 0;
  let derivedBearing = null;
  if (prevHasCoord && currHasCoord) {
    movementMeters = haversineMeters(Number(prev.latitude), Number(prev.longitude), Number(latitude), Number(longitude));
    if (movementMeters >= HEADING_BEARING_MIN_DISTANCE_M) {
      derivedBearing = bearingDegrees(Number(prev.latitude), Number(prev.longitude), Number(latitude), Number(longitude));
    }
  }

  if (toFiniteNumber(speed, 0) < HEADING_FREEZE_SPEED_KMH && prevHeading !== null) {
    return { heading: prevHeading, movementMeters };
  }

  if (packetHeading === null && derivedBearing === null) {
    return { heading: prevHeading ?? 0, movementMeters };
  }

  if (prevHeading === null) {
    return { heading: packetHeading ?? derivedBearing ?? 0, movementMeters };
  }

  const candidate = derivedBearing ?? packetHeading ?? prevHeading;
  const delta = shortestDeltaDeg(prevHeading, candidate);
  if (Math.abs(delta) > HEADING_MAX_STEP_DEG && movementMeters < 35) {
    return {
      heading: normalizeHeading360(prevHeading + Math.sign(delta) * HEADING_MAX_STEP_DEG, prevHeading),
      movementMeters,
    };
  }

  return { heading: normalizeHeading360(candidate, prevHeading), movementMeters };
};

export const resolveTrackingState = ({
  prev = null,
  speed = 0,
  accStatus = null,
  timestampMs = Date.now(),
  latitude,
  longitude,
  heading = 0,
  vehicleStatus = null,
  movementState = null,
  movementStatus = null,
  direction = null,
  turnType = null,
} = {}) => {
  const numericSpeed = Math.max(0, toFiniteNumber(speed, 0));
  const safeTimestampMs = Number.isFinite(Number(timestampMs)) ? Number(timestampMs) : Date.now();
  const normalizedAcc = toBoolean(accStatus, null);
  const prevLastMovingAtMs = Number(prev?.lastMovingAtMs || prev?.deviceTimestampMs || 0);
  const prevLastZeroSpeedAtMs = Number(prev?.lastZeroSpeedAtMs || 0);
  const lastMovingAtMs = numericSpeed >= MOVING_SPEED_KMH ? safeTimestampMs : prevLastMovingAtMs;
  const lastZeroSpeedAtMs = numericSpeed === 0 ? (prevLastZeroSpeedAtMs || safeTimestampMs) : 0;
  const zeroSpeedDurationMs = numericSpeed === 0 && lastZeroSpeedAtMs > 0 ? safeTimestampMs - lastZeroSpeedAtMs : 0;
  const resolvedVehicleStatus = inferVehicleStatus({
    vehicleStatus,
    movementStatus,
    speed: numericSpeed,
    accStatus: normalizedAcc,
    prev,
    timestampMs: safeTimestampMs,
  });
  const previousStatus = normalizeVehicleStatus(prev?.vehicleStatus);
  const statusSinceMs = previousStatus === resolvedVehicleStatus
    ? Number(prev?.statusSinceMs || safeTimestampMs)
    : safeTimestampMs;

  const headingDecision = resolveHeading({
    prev,
    latitude,
    longitude,
    speed: numericSpeed,
    heading,
  });
  const resolvedHeading = normalizeHeading360(headingDecision.heading, 0);

  const providedMovementState = normalizeMovementState(movementState);
  const resolvedMovementState =
    providedMovementState ||
    resolveMovementState({
      vehicleStatus: resolvedVehicleStatus,
      movementStatus,
      speed: numericSpeed,
      accStatus: normalizedAcc,
      lastMovingAtMs,
      timestampMs: safeTimestampMs,
    });

  const resolvedDirection = normalizeDirection(direction) || headingToDirection(resolvedHeading);

  const providedTurnType = normalizeTurnType(turnType);
  let resolvedTurnType = providedTurnType;

  const prevHeading = Number.isFinite(Number(prev?.heading))
    ? normalizeHeading360(prev.heading, resolvedHeading)
    : null;
  const turnDelta = prevHeading === null ? 0 : Math.abs(shortestDeltaDeg(prevHeading, resolvedHeading));

  let turnCandidateType = null;
  let turnCandidateCount = 0;
  let turnCandidateAtMs = 0;

  if (!resolvedTurnType) {
    const turnCandidate =
      resolvedMovementState === "moving" &&
      numericSpeed >= TURN_MIN_SPEED_KMH &&
      headingDecision.movementMeters >= TURN_MIN_DISTANCE_M
        ? classifyTurn(turnDelta)
        : null;

    const prevTurnType = String(prev?.turnCandidateType || "");
    const prevTurnCount = Number(prev?.turnCandidateCount || 0);
    const prevTurnAt = Number(prev?.turnCandidateAtMs || 0);
    const sameCandidate = turnCandidate && prevTurnType === turnCandidate;
    const inWindow = prevTurnAt > 0 && safeTimestampMs - prevTurnAt <= TURN_CONFIRM_WINDOW_MS;

    turnCandidateType = turnCandidate || null;
    turnCandidateCount = turnCandidate ? (sameCandidate && inWindow ? prevTurnCount + 1 : 1) : 0;
    turnCandidateAtMs = turnCandidate ? safeTimestampMs : 0;
    resolvedTurnType = turnCandidate && turnCandidateCount >= 2 ? turnCandidate : null;
  }

  return {
    vehicleStatus: resolvedVehicleStatus,
    statusSinceMs,
    movementState: resolvedMovementState,
    heading: resolvedHeading,
    direction: resolvedDirection,
    turnType: resolvedTurnType,
    turnDelta,
    turnCandidateType,
    turnCandidateCount,
    turnCandidateAtMs,
    movementMeters: headingDecision.movementMeters,
    lastMovingAtMs,
    lastZeroSpeedAtMs,
    zeroSpeedDurationMs,
    latitude: toFiniteNullable(latitude),
    longitude: toFiniteNullable(longitude),
    deviceTimestampMs: safeTimestampMs,
    speed: numericSpeed,
    accStatus: normalizedAcc,
  };
};
